import { getCitySkyline } from "@/lib/citySkylines";

/**
 * Renders the "city" slot of a hero photo composite for a given city: the
 * <picture> webp+jpg plumbing (width/height, fetchpriority) plus whatever
 * CSS class the caller passes (typically .hero-photo-city -- the diagonal
 * mask, object-position, and warmth filter defined in index.css, which are
 * already city-agnostic and shared as-is).
 *
 * Introduced 2026-09-24 alongside the Houston "Be Someone" bridge photo so
 * this asset-selection + markup pattern lives in one place instead of
 * being hardcoded per page (Home.jsx previously inlined
 * `<HeroPicture base="city-panel" .../>`). A future page that needs a
 * different city's skyline (Dallas, Austin, Fort Worth, San Antonio,
 * Lubbock) sources+grades that city's photo, adds one entry to
 * frontend/src/lib/citySkylines.js, and passes `city="dallas"` here --
 * no CSS/markup duplication. The homepage hero always passes
 * `city="houston"` explicitly; this component's `city` default exists only
 * as a safety fallback, not to make the homepage's Houston choice implicit.
 *
 * Decorative only, matching the rest of the hero band: alt="" always, no
 * semantic role -- the parent hero section carries aria-hidden/presentation
 * and the page's H1/support text carry the actual meaning.
 */
export default function CitySkylinePanel({
  city = "houston",
  className = "hero-photo-city",
  testId,
  eager = true,
}) {
  const skyline = getCitySkyline(city);
  const [w, h] = skyline.dims;
  return (
    <picture>
      <source srcSet={`/hero/${skyline.base}.webp`} type="image/webp" />
      <img
        src={`/hero/${skyline.base}.jpg`}
        alt=""
        width={w}
        height={h}
        className={className}
        loading={eager ? "eager" : undefined}
        fetchPriority={eager ? "high" : undefined}
        data-testid={testId}
      />
    </picture>
  );
}
